'use client';

import { useState, cloneElement, type FC, isValidElement } from 'react';
import { Button, Pill, Popover, ResultList } from '@/_components';
import styles from './Dropdown.module.css';
import type { DropdownProps } from './Dropdown.types';

/**
 * Dropdown component. {@link items} will be displayed in a popover as a list of
 * selectable items.
 *
 * If the child is a {@link Button} or {@link Pill} with a caret `iconRight`, the
 * caret will change directions in response to the visibility state of the dropdown.
 */
const Dropdown: FC<DropdownProps> = ({
  isHidden = false,
  onSelect,
  children,
  items,
  isDisabled,
  placement = 'bottom-end',
}) => {
  const [isVisible, setVisible] = useState<boolean>(isHidden);

  // If child is a component that potentially takes a caret icon, respond to visible state
  let triggerContent = children;
  if (
    !isDisabled &&
    isValidElement(children) &&
    (children?.type === Button || children?.type === Pill)
  ) {
    triggerContent = cloneElement(children, {
      ...(children?.props as any),
      // Switch caret direction
      ...((children?.props as any)?.iconRight === 'caret-down' &&
        isVisible && { iconRight: 'caret-up' }),
      ...((children?.props as any)?.iconRight === 'caret-up' &&
        isVisible && { iconRight: 'caret-down' }),
      ...((children?.props as any)?.iconRight === 'caret-right' &&
        isVisible && { iconRight: 'caret-left' }),
      ...((children?.props as any)?.iconRight === 'caret-left' &&
        isVisible && { iconRight: 'caret-right' }),
    });
  }

  return (
    <Popover
      placement={placement}
      open={isVisible}
      onOpenChange={setVisible}
      isDisabled={isDisabled}
      role="menu"
    >
      <Popover.Trigger>{triggerContent}</Popover.Trigger>
      <Popover.Content padding={2}>
        <div className={styles.dropdownNav}>
          <ResultList
            items={items}
            onSelect={(event: any) => {
              setVisible(false);
              if (onSelect) onSelect(event);
            }}
            itemRole="menuitem"
            card={false} // TODO: refactor `ResultList` and `Input` such that this is not necessary
          />
        </div>
      </Popover.Content>
    </Popover>
  );
};

export { Dropdown };
